import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { CLASSIFICATION_LABEL, Pill, RESPONSE_LABEL, classificationTone } from '../../components/audit/AuditVocabulary.js';
import { apiFetchBlob } from '../../lib/api.js';
import {
  fetchAudit,
  fetchAudits,
  fetchChecklist,
  fetchCorrectiveActions,
  fetchEvidence,
  fetchFindings,
  type AuditFinding,
} from '../../lib/audit-api.js';
import { t, formatDateTime } from '../../lib/i18n.js';

/**
 * What a Guest Auditor sees (spec §6, SEC-8): the audits they are invited to
 * read, and each one read-only — its meetings, checklist answers, evidence,
 * findings and corrective actions. Nothing here writes, and the server refuses
 * a guest anything else regardless. Every page opened is recorded.
 */

function when(value: string | null) {
  return value ? formatDateTime(value, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

export function GuestAuditList() {
  const audits = useQuery({ queryKey: ['audits', 'invited'], queryFn: () => fetchAudits() });
  const rows = audits.data?.audits ?? [];

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Audits you are invited to']} />
      <main className="mx-auto max-w-4xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Audits you are invited to")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("You can read these until your invitation ends or the audit closes. What you open is recorded.")}
        </p>
        {audits.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {audits.error && <p className="mt-6">{t("Your audits could not be loaded.")}</p>}
        {audits.data && rows.length === 0 && (
          <p className="mt-6 text-sm text-content-secondary">
            {t("You are not invited to any open audit at the moment. Your invitation may have ended, or the audit has closed.")}
          </p>
        )}
        <ul className="mt-6 list-none space-y-2 p-0">
          {rows.map((audit) => (
            <li key={audit.id} className="rounded border border-border p-3">
              <Link to={`/audit/invited/${audit.id}`} className="font-medium text-ag-purple">
                {audit.referenceNo} — {audit.title}
              </Link>
              <span className="ms-2 text-sm text-content-secondary">{audit.status.replace('_', ' ')}</span>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

export function GuestAuditView() {
  const { id } = useParams<{ id: string }>();
  const audit = useQuery({ queryKey: ['audit', id], queryFn: () => fetchAudit(id!), enabled: Boolean(id), retry: false });
  const checklist = useQuery({ queryKey: ['checklist', id], queryFn: () => fetchChecklist(id!), enabled: Boolean(audit.data) });
  const evidence = useQuery({ queryKey: ['audit-evidence', id], queryFn: () => fetchEvidence(id!), enabled: Boolean(audit.data) });
  const findings = useQuery({ queryKey: ['audit-findings', id], queryFn: () => fetchFindings(id!), enabled: Boolean(audit.data) });
  const record = audit.data?.audit;

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Audits you are invited to', record?.referenceNo ?? '…']} />
      <main className="mx-auto max-w-4xl px-4 pb-12 pt-4">
        {audit.isLoading && <p className="text-content-secondary">{t("Loading…")}</p>}
        {audit.error && (
          <section role="status" className="rounded-md border border-border bg-tone-info-surface p-6">
            <h1 className="m-0 text-lg font-semibold">{t("This audit is not open to you")}</h1>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {t("Your invitation may have ended, or the audit has closed.")}{' '}<Link to="/audit/invited">{t("Your audits")}</Link>
            </p>
          </section>
        )}
        {record && (
          <>
            <h1 className="m-0 text-2xl font-semibold">{record.title}</h1>
            <p className="mt-1 text-sm text-content-secondary">
              {record.referenceNo} · {record.status.replace('_', ' ')}{' '}{t("· read-only")}
            </p>

            <section className="mt-6" aria-labelledby="meetings">
              <h2 id="meetings" className="text-lg font-semibold">{t("Meetings")}</h2>
              <dl className="text-sm">
                <dt className="font-medium">{t("Opening meeting")}</dt>
                <dd className="mb-2 ms-0">{when(record.openingMeetingAt)} {record.openingMeetingMinutes ? `— ${record.openingMeetingMinutes}` : ''}</dd>
                <dt className="font-medium">{t("Closing meeting")}</dt>
                <dd className="ms-0">{when(record.closingMeetingAt)} {record.closingMeetingMinutes ? `— ${record.closingMeetingMinutes}` : ''}</dd>
              </dl>
            </section>

            <section className="mt-6" aria-labelledby="checklist">
              <h2 id="checklist" className="text-lg font-semibold">{t("Checklist answers")}</h2>
              {(checklist.data?.responses ?? []).length === 0 ? (
                <p className="text-sm text-content-secondary">{t("No checklist answers yet.")}</p>
              ) : (
                <ul className="list-none space-y-2 p-0 text-sm">
                  {checklist.data!.responses.map((row) => (
                    <li key={row.id} className="rounded border border-border p-2">
                      {row.clause ? <span className="text-content-secondary">{row.clause} · </span> : null}
                      {row.question ?? t("Checklist item")} — <strong>{RESPONSE_LABEL[row.response] ?? row.response}</strong>
                      {row.notes ? <p className="mb-0 mt-1 text-content-secondary">{row.notes}</p> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-6" aria-labelledby="evidence">
              <h2 id="evidence" className="text-lg font-semibold">{t("Evidence")}</h2>
              {(evidence.data?.evidence ?? []).length === 0 ? (
                <p className="text-sm text-content-secondary">{t("No evidence recorded.")}</p>
              ) : (
                <ul className="list-none space-y-2 p-0 text-sm">
                  {evidence.data!.evidence.map((item) => (
                    <li key={item.id} className="rounded border border-border p-2">
                      <span className="capitalize">{item.evidenceType}</span> · {when(item.capturedAt)}
                      {item.description ? <p className="mb-0 mt-1">{item.description}</p> : null}
                      {item.fileId ? <OpenFile fileId={item.fileId} /> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-6" aria-labelledby="findings">
              <h2 id="findings" className="text-lg font-semibold">{t("Findings")}</h2>
              {(findings.data?.findings ?? []).length === 0 ? (
                <p className="text-sm text-content-secondary">{t("No findings raised.")}</p>
              ) : (
                <ul className="list-none space-y-3 p-0 text-sm">
                  {findings.data!.findings.map((finding) => (
                    <GuestFinding key={finding.id} finding={finding} />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function GuestFinding({ finding }: { finding: AuditFinding }) {
  const cars = useQuery({ queryKey: ['cars', finding.id], queryFn: () => fetchCorrectiveActions(finding.id) });
  return (
    <li className="rounded border border-border p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <strong>{finding.referenceNo}</strong>
        <Pill tone={classificationTone(finding.classification)}>{t(CLASSIFICATION_LABEL[finding.classification])}</Pill>
        {finding.clause ? <span className="text-content-secondary">{t("clause")}{' '}{finding.clause}</span> : null}
      </div>
      <p className="mb-0 mt-1">{finding.description}</p>
      {(cars.data?.correctiveActions ?? []).length > 0 && (
        <ul className="mt-2 list-disc ps-5">
          {cars.data!.correctiveActions.map((car) => (
            <li key={car.id}>
              {car.referenceNo} · {car.status.replace(/_/g, ' ')}
              {car.targetCompletionDate ? t(" · target {0}", { 0: car.targetCompletionDate }) : ''}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Files are fetched with the sign-in token, then opened: a plain link would carry none. */
function OpenFile({ fileId }: { fileId: string }) {
  return (
    <button
      type="button"
      className="mt-1 block text-ag-purple underline"
      onClick={() => {
        void apiFetchBlob(`/files/${fileId}`).then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener');
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        });
      }}
    >
      {t("Open the file")}
    </button>
  );
}
